import React, { useState, useEffect, useRef } from "react"
import style from "./iframe.mod.scss"
import { graphql } from "gatsby"
import IframeResizer from "iframe-resizer-react"
import { Loading } from "@components"
import { motion } from "framer-motion"

// Height used until Zoho reports the real one, and if it never does.
const ZOHO_FALLBACK_HEIGHT = 480
const ZOHO_MESSAGE_TIMEOUT = 5000

// Zoho forms can't be measured by iframe-resizer (that needs a script inside the
// embedded page). Instead Zoho posts "<formperma>|<height>" back to the parent,
// but only when the iframe URL carries zf_rszfm=1.
const getZohoFormSrc = src => {
  try {
    const url = new URL(src)

    if (
      !url.hostname.startsWith("forms.zoho") ||
      !url.pathname.includes("/formperma/")
    ) {
      return null
    }

    url.searchParams.set("zf_rszfm", "1")
    return url.toString()
  } catch {
    return null
  }
}

const useZohoAutoHeight = src => {
  const ref = useRef(null)
  const [height, setHeight] = useState(null)

  useEffect(() => {
    if (!src) return

    const perma = src.split("/formperma/")[1]?.split(/[?#/]/)[0]
    if (!perma) return

    const onMessage = event => {
      if (!event.origin.startsWith("https://forms.zoho")) return
      if (typeof event.data !== "string") return

      // "<formperma>|<height>" — a third segment means the form changed page
      // and wants to be scrolled back to the top.
      const [messagePerma, contentHeight, scrollIntoView] = event.data.split(
        "|"
      )
      if (messagePerma !== perma || !contentHeight) return

      // Zoho's own embed adds 15px so the last field isn't clipped.
      setHeight(parseInt(contentHeight, 10) + 15)

      if (scrollIntoView) ref.current?.scrollIntoView()
    }

    const timer = setTimeout(
      () => setHeight(current => current ?? ZOHO_FALLBACK_HEIGHT),
      ZOHO_MESSAGE_TIMEOUT
    )

    window.addEventListener("message", onMessage, false)

    return () => {
      window.removeEventListener("message", onMessage)
      clearTimeout(timer)
    }
  }, [src])

  return [ref, height]
}

const Iframe = ({ resize = false, isPage = false, ...props }) => {
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(false)
  }, [])

  const variants = {
    hidden: {
      opacity: 0,
      scale: 0,
      tabIndex: -1,
      transition: {
        delay: 0.5,
        opacity: "linear"
      }
    },
    visible: {
      opacity: 1,
      scale: 1,
      tabIndex: 0,
      transition: {
        delay: 0.5,
        opacity: "linear"
      }
    }
  }

  // Remove scrolling from props to prevent it from being passed to either iframe or IframeResizer
  const { scrolling, ...rest } = props

  const zohoSrc = getZohoFormSrc(rest.src)
  const [zohoRef, zohoHeight] = useZohoAutoHeight(zohoSrc)

  if (zohoSrc) {
    const ready = !loading && zohoHeight !== null

    return (
      <div className={style.iframe}>
        <div className={style.iframe__inner}>
          <motion.div
            initial={"visible"}
            animate={ready ? "hidden" : "visible"}
            variants={variants}
            className={style.iframe__loading}
          >
            <Loading />
          </motion.div>
          <motion.div
            initial={"hidden"}
            animate={ready ? "visible" : "hidden"}
            variants={variants}
          >
            <iframe
              ref={zohoRef}
              referrerPolicy="strict-origin-when-cross-origin"
              {...rest}
              src={zohoSrc}
              scrolling="no"
              style={{
                display: "block",
                width: "100%",
                height: `${zohoHeight ?? ZOHO_FALLBACK_HEIGHT}px`
              }}
            />
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className={style.iframe}>
      {resize && !isPage ? (
        <div className={style.iframe__inner}>
          <motion.div
            initial={"visible"}
            animate={loading ? "visible" : "hidden"}
            variants={variants}
            className={style.iframe__loading}
          >
            <Loading />
          </motion.div>
          <motion.div
            initial={"hidden"}
            animate={loading ? "hidden" : "visible"}
            variants={variants}
          >
            <IframeResizer
              referrerPolicy="strict-origin-when-cross-origin"
              {...rest}
              scrolling="omit"
              style={
                isPage
                  ? {
                      width: "100%",
                      height: "80vh",
                      overflow: "auto",
                      minHeight: "480px"
                    }
                  : {
                      width: "1px",
                      minWidth: "100%",
                      minHeight: "480px"
                    }
              }
            />
          </motion.div>
        </div>
      ) : (
        <iframe
          referrerPolicy="strict-origin-when-cross-origin"
          {...rest}
          width={isPage ? "100%" : "100%"}
          height={isPage ? "80vh" : "100%"}
          style={
            isPage
              ? {
                  overflow: "auto",
                  overflowX: "hidden",
                  height: "80vh",
                  minHeight: "480px",
                  width: "100%",
                  scrollBehavior: "smooth"
                }
              : {}
          }
        />
      )}
    </div>
  )
}

export default Iframe

export const query = graphql`
  fragment FormIframe on DatoCmsEmbed {
    id
    title
    embed
    isPage
    padding
    model {
      apiKey
    }
    __typename
  }
`
